import { InfiniteData, useQueryClient } from 'react-query'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useMutation } from 'hooks/useMutation'
import { useQuery } from 'hooks/useQuery'

import {
  createRoom as createRoomQuery,
  fetchRoom,
  fetchRoomByParticipants,
  updateLastViewed as updateLastViewedQuery,
} from 'utils/queries'
import { Toast } from 'utils/toast'

import { Room } from 'types/chat'
import { PartialUser } from 'types/user'

export const useDMRoom = ({
  roomId,
  roomParticipants,
}: {
  roomId?: string
  roomParticipants?: PartialUser[]
}) => {
  const { data: currentUser } = useCurrentUser()
  const queryClient = useQueryClient()

  const invalidateRoomQueries = () => {
    queryClient.setQueryData(['rooms', currentUser?.id], undefined)
    queryClient.invalidateQueries('rooms')
  }

  const roomQuery = useQuery<Room | null>(
    ['room', roomId, roomParticipants],
    () => {
      if (roomId) {
        return fetchRoom({ roomId })
      }
      if (roomParticipants) {
        return fetchRoomByParticipants({ participants: roomParticipants })
      }
      return Promise.resolve(null)
    },
    {
      enabled: !!(roomId || roomParticipants),
    },
  )

  const createRoom = useMutation<{ id: string }, { participants: PartialUser[] }>(
    ({ participants }) => createRoomQuery({ participants }),
    {
      onSuccess: () => invalidateRoomQueries(),
      onError: () => {
        Toast.error('Unable to create chat room', 'Please try again')
      },
    },
  )

  const updateLastViewed = useMutation<unknown, { userId: string }>(
    ({ userId }) => (roomId ? updateLastViewedQuery({ roomId, userId }) : Promise.resolve(null)),
    {
      // Set local viewed to current time and clear unread count
      onSuccess: (data, { userId }) => {
        if (roomId) {
          const roomData: InfiniteData<Room[]> = queryClient.getQueryData(['rooms', userId]) || {
            pageParams: [],
            pages: [],
          }

          const updatedRoomData = {
            ...roomData,
            pages: roomData.pages.map((roomPage) =>
              roomPage.map((room) =>
                room.id !== roomId
                  ? room
                  : {
                      ...room,
                      meta: {
                        ...room.meta,
                        lastViewed: Date.now(),
                        newMessageCount: 0,
                      },
                    },
              ),
            ),
          }

          queryClient.setQueryData(['rooms', userId], updatedRoomData)
        }
      },
    },
  )

  return {
    ...roomQuery,

    // Mutations
    createRoom,
    updateLastViewed,
  }
}
